import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface User {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  pushTokenType?: 'expo' | 'fcm' | 'apns';
  notificationPreferences?: {
    appUpdatesPush?: boolean;
    appUpdatesEmail?: boolean;
    userMessagingPush?: boolean;
    userMessagingEmail?: boolean;
    newPublicStation?: boolean;
  };
}

const NotificationManager: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState({
    users: false,
    sending: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [form, setForm] = useState({
    title: '',
    message: '',
    notificationType: 'userMessaging',
    targetType: 'selected',
    selectedUsers: [] as string[],
    targetRole: 'user',
    imageUrl: ''
  });
  
  useEffect(() => {
    fetchUsers();
  }, []);
  
  const fetchUsers = async () => {
    setLoading(prev => ({ ...prev, users: true }));
    try {
      const response = await api.get('/notifications/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(t('notifications.errors.fetchUsers'));
    } finally {
      setLoading(prev => ({ ...prev, users: false }));
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    
    // Reset selected users when changing target type
    if (name === 'targetType' && value !== 'selected') {
      setForm(prev => ({ ...prev, selectedUsers: [] }));
    }
    
    // Clear error/success messages when form changes
    setError('');
    setSuccess('');
  };
  
  const handleUserSelection = (userId: string) => {
    setForm(prev => {
      const isSelected = prev.selectedUsers.includes(userId);
      const selectedUsers = isSelected
        ? prev.selectedUsers.filter(id => id !== userId)
        : [...prev.selectedUsers, userId];
      
      return { ...prev, selectedUsers };
    });
  };
  
  const handleSelectAll = () => {
    if (form.selectedUsers.length === users.length) {
      // Deselect all
      setForm(prev => ({ ...prev, selectedUsers: [] }));
    } else {
      // Select all
      setForm(prev => ({ ...prev, selectedUsers: users.map(user => user._id) }));
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!form.title.trim() || !form.message.trim()) {
      setError('Title and message are required');
      return;
    }
    
    if (form.targetType === 'selected' && form.selectedUsers.length === 0) {
      setError('Please select at least one user');
      return;
    }
    
    try {
      setLoading(prev => ({ ...prev, sending: true }));
      setError('');
      setSuccess('');
      
      let response;
      
      // Create notification data object with optional image
      const notificationData = {
        title: form.title,
        message: form.message,
        notificationType: form.notificationType,
        imageUrl: form.imageUrl || undefined
      };
      
      switch (form.targetType) {
        case 'selected':
          response = await api.post('/notifications/send', {
            userIds: form.selectedUsers,
            ...notificationData
          });
          break;
          
        case 'role':
          response = await api.post('/notifications/send-to-role', {
            role: form.targetRole,
            ...notificationData
          });
          break;
          
        case 'all':
          response = await api.post('/notifications/send-to-all', {
            ...notificationData
          });
          break;
      }
      
      console.log('Notification response:', response?.data);
      setSuccess(response?.data?.message || 'Notifications sent successfully');
      
      // Reset form
      setForm(prev => ({
        ...prev,
        title: '',
        message: '',
        imageUrl: ''
      }));
    } catch (error: any) {
      console.error('Error sending notifications:', error);
      setError(error.response?.data?.error || 'Failed to send notifications');
    } finally {
      setLoading(prev => ({ ...prev, sending: false }));
    }
  };
  
  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-6">{t('admin.sendPushNotifications')}</h2>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.notificationType')}
            </label>
            <select
              name="notificationType"
              value={form.notificationType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="userMessaging">{t('admin.notificationTypes.userMessaging')}</option>
              <option value="appUpdate">{t('admin.notificationTypes.appUpdate')}</option>
              <option value="newStation">{t('admin.notificationTypes.newStation')}</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {t('admin.notificationTypeHelp')}
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.targetUsers')}
            </label>
            <select
              name="targetType"
              value={form.targetType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="selected">{t('admin.targetTypes.selected')}</option>
              <option value="role">{t('admin.targetTypes.role')}</option>
              <option value="all">{t('admin.targetTypes.all')}</option>
            </select>
          </div>
          
          {form.targetType === 'role' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.userRole')}
              </label>
              <select
                name="targetRole"
                value={form.targetRole}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="user">{t('admin.roles.user')}</option>
                <option value="admin">{t('admin.roles.admin')}</option>
                <option value="operator">{t('admin.roles.operator')}</option>
              </select>
            </div>
          )}
          
          {form.targetType === 'selected' && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('admin.selectUsers')}
                </label>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  {form.selectedUsers.length === users.length
                    ? t('admin.deselectAll')
                    : t('admin.selectAll')}
                </button>
              </div>
              
              {loading.users ? (
                <div className="p-4 text-center">{t('common.loading')}</div>
              ) : users.length > 0 ? (
                <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-md">
                  {users.map(user => (
                    <div 
                      key={user._id}
                      className={`flex items-center p-3 border-b border-gray-200 hover:bg-gray-50 cursor-pointer ${
                        form.selectedUsers.includes(user._id) ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => handleUserSelection(user._id)}
                    >
                      <input
                        type="checkbox"
                        checked={form.selectedUsers.includes(user._id)}
                        onChange={() => {}}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                        <div className="text-xs text-gray-500 flex items-center mt-1">
                          <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs mr-2">
                            {user.pushTokenType || 'expo'}
                          </span>
                          {user.phone && <span className="mr-2">{user.phone}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  {t('admin.noUsersWithPushTokens')}
                </div>
              )}
              
              <p className="mt-2 text-sm text-gray-500">
                {t('admin.selectedUsers', { count: form.selectedUsers.length })}
              </p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.notificationTitle')}
            </label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              placeholder={t('admin.notificationTitlePlaceholder')}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.notificationMessage')}
            </label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              placeholder={t('admin.notificationMessagePlaceholder')}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.imageUrl')} ({t('common.optional')})
            </label>
            <input
              type="text"
              name="imageUrl"
              value={form.imageUrl}
              onChange={handleChange}
              placeholder="https://example.com/image.jpg"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-sm text-gray-500">
              {t('admin.imageUrlHelp')}
            </p>
          </div>
          
          <button
            type="submit"
            disabled={loading.sending}
            className={`w-full bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              loading.sending ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {loading.sending 
              ? t('common.sending') 
              : t('admin.sendNotifications')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default NotificationManager; 